import React, { ReactNode, FC, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHotkeys } from 'react-hotkeys-hook';

import NavigationBar from '../navigation/NavigationBar';
import ToastMessageContainer from './../../containers/ToastMessage/ToastMessageContainer';
import ModalContainer from '../../common/components/ModalContainer';
import TableDashboardLayout from '../../ui-components/TableDashboardLayout';
import store from '../../store';
import { setSelectedWorklistFilters } from '../../store/queryParams/queryParamsActions';
import { setSearchTermRequest } from '../../store/searchTerm/searchTermAction';
import { setWorklistQueryParams } from '../../store/worklist/worklistActions';
import {
  getUserPreferencesWorklist,
  getPageSize
} from '../../store/userPreferences/userPreferencesSelector';
import { worklistSelectedIds } from '../../store/worklist/worklistSelector';
import { GlobalAlerts } from '../../store/alerts/types';
import { useFocus } from '../../utils/helperHooks/useFocus';
import { hostResolver } from '../../utils/envResolver';
import NewTableFilter from '../NewTableFilter';
import { ModalProvider } from '@providers/modal-provider';

interface Props {
  isAuthenticated: boolean;
  locationPath: string;
  children?: ReactNode;
  alerts: [GlobalAlerts] | [];
}

const Layout: FC<Props> = ({ isAuthenticated, locationPath, children = null, alerts }) => {
  // TODO: rdar://103049975 (MTAR: Enable keyboard hotkey events in Shakespeare UI)
  // Remove once all hot keys are hooked up
  const host = hostResolver();
  const hotkeysEnabled = ['localhost', 'itms7'].includes(host);
  const [searchInputRef, setSearchInputFocus] = useFocus();
  const selectedWorklistTasks = useSelector(worklistSelectedIds);
  const pageSize = useSelector(getPageSize);

  const userPreferencesWorklist = useSelector(getUserPreferencesWorklist);

  const handleUnreadSelectedTasks = () => {
    console.log('unread', selectedWorklistTasks);
  };

  const handleReadSelectedTasks = () => {
    console.log('read', selectedWorklistTasks);
  };

  const handleSearchInput = () => {
    setSearchInputFocus();
  };

  useHotkeys('u', handleUnreadSelectedTasks, { enabled: hotkeysEnabled });
  useHotkeys('r', handleReadSelectedTasks, { enabled: hotkeysEnabled });
  useHotkeys('s', handleSearchInput, { keyup: true });

  const isFilterVisible = useCallback((): boolean => {
    if (
      locationPath === '/tasks' ||
      locationPath === '/tasks/' ||
      locationPath === '/worklist' ||
      locationPath === '/worklist/'
      // TODO Improve in the future by handling slashes at the end of an endpoint in RouteCreator.tsx e.g. <Redirect strict from={url + '/'} to={url} />
    ) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return true;
    }
    return false;
  }, [locationPath]);

  const filterRouteForLocation = (): ReactNode => {
    if (
      locationPath === '/tasks' ||
      locationPath === '/tasks/' ||
      locationPath === '/worklist' ||
      locationPath === '/worklist/'
    ) {
      return (
        <ModalProvider>
          <NewTableFilter />
        </ModalProvider>
      );
    }
    return null;
  };

  const {
    sortDirection,
    sortField,
    cursor,
    searchTerm,
    filters = [],
    filtersV2 = []
  } = userPreferencesWorklist;

  useEffect(() => {
    if (isAuthenticated && isFilterVisible()) {
      store.dispatch(setSelectedWorklistFilters(filters));
      store.dispatch(
        setWorklistQueryParams({
          sortDirection,
          sortField,
          cursor,
          searchTerm,
          filters: filtersV2
        })
      );
      store.dispatch(setSearchTermRequest(searchTerm));
    }
  }, [
    isAuthenticated,
    sortDirection,
    sortField,
    cursor,
    searchTerm,
    filters,
    pageSize,
    filtersV2,
    isFilterVisible
  ]);

  const renderBanner = (): [GlobalAlerts] | [] => {
    return alerts;
  };

  return (
    <>
      <TableDashboardLayout
        filter={filterRouteForLocation()}
        isFilterVisible={isFilterVisible()}
        isFilterBodyVisible={true}
        header={isAuthenticated && <NavigationBar searchInputRef={searchInputRef} />}
        alerts={renderBanner()}
        dashboard={<div className="mpe-container-full-height">{children}</div>}
      >
        <ToastMessageContainer />
        <ModalContainer />
      </TableDashboardLayout>
    </>
  );
};

export default Layout;
